import React, { useState, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Form, Button, Spinner, Container, Row, Col } from 'react-bootstrap';
import DateFilterInput from '../Utils/DateFilterInput';
import InputField from '../Utils/InputField';
import SelectInput from '../Utils/SelectInput';
import { convertDateToISO, getCurrentDate, getCurrentDateMinusDays } from '../Utils/utils';
import { ReportTransfer } from '../Enum/ReportTransferEnum';
import { romaneioReportPdf, romaneioReportCsv } from '../../services/Reports/reportsService';
import PersonInput from '../Utils/PersonInput';
import ProductInput from '../Utils/ProductInput';
import { ReportOrderBy } from '../Enum/ReportOrderByEnum';
import { usePermissions } from '../../hooks/usePermissions';
import { usePermissionsContext } from '../../context/PermissionsContext';

const Romaneio = () => {
  const { checkPermission, ToastComponent, hasPermission } = usePermissions();
  const { permissions } = usePermissionsContext();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCsv, setIsLoadingCsv] = useState(false);
  const { register, handleSubmit, formState: { errors }, control, watch, setValue, getValues, trigger } = useForm({
    defaultValues: {
      transferOptions: 'ALL',
      reportOrderBy: 'PERSON',
    }
  });
  const watchedFields = watch(['licensePlate', 'person', 'driver', 'product', 'initialDate', 'finalDate']);
  const [licensePlate, person, driver, product] = watchedFields;
  const isAnyOtherFieldFilled = licensePlate || person || driver || product;

  // Recalcula permissões sempre que o objeto permissions mudar
  const canView = useMemo(() => hasPermission('reportsView'), [hasPermission, permissions]);

  const formatPayload = (data) => {
    const getSafeDate = (dateStr, fallback) => {
      if (!dateStr || dateStr.trim() === '') {
        return fallback;
      }
      const isValid = /^\d{2}\/\d{2}\/\d{4}$/.test(dateStr);
      return isValid ? convertDateToISO(dateStr) : fallback;
    };

    const defaultInitialDate = getCurrentDateMinusDays(30);
    const defaultFinalDate = getCurrentDate();

    return {
      ...data,
      initialDate: getSafeDate(data?.initialDate, defaultInitialDate),
      finalDate: getSafeDate(data?.finalDate, defaultFinalDate),
      licensePlate: data.licensePlate || null,
      person: data.person?.id || null,
      driver: data.driver?.id || null,
      product: data.product?.id || null
    };
  };

  const onSubmit = async (data) => {
    if (!checkPermission('reportsView')) {
      return;
    }
    setIsLoading(true);
    try {
      const formattedPayload = formatPayload(data);
      await romaneioReportPdf(formattedPayload);
    } 
    catch (error) {
      console.log(error);
    } 
    finally {
      setIsLoading(false);
    }
  };

  const onExportCsv = async () => {
    if (!checkPermission('reportsView')) {
      return;
    }
    
    const isValid = await trigger(['initialDate', 'finalDate']);
    if (!isValid) {
      return;
    }
    
    setIsLoadingCsv(true);
    try {
      const formData = getValues();
      const formattedPayload = formatPayload(formData);
      await romaneioReportCsv(formattedPayload);
    } 
    catch (error) {
      console.log(error);
    } 
    finally {
      setIsLoadingCsv(false);
    }
  };

  if (!canView) {
    return (
      <>
        <ToastComponent />
        <Container>
          <h3 className="mb-4 mt-2 text-center text-md-start">Relatório de Romaneios / Transferências</h3>
        </Container>
      </>
    );
  }

  return (
    <Container>
      <ToastComponent />
      <h3 className="mb-4 mt-2 text-center text-md-start">Relatório de Romaneios / Transferências</h3>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Group className="mb-3">
          <Row>
            <Col md={2}>
              <Controller
                name="initialDate"
                control={control}
                rules={{
                  validate: (value) => {
                    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
                    if (!isAnyOtherFieldFilled && !value) return 'A data inicial é obrigatória.';
                    if (value && !dateRegex.test(value)) return 'Data inválida';
                    return true;
                  }
                }}
                render={({ field, fieldState }) => (
                  <DateFilterInput
                    label="Data Inicial"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error}
                    required={true}
                  />
                )}
              />
            </Col>
            <Col md={2}>
              <Controller
                name="finalDate"
                control={control}
                rules={{
                  validate: (value) => {
                    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
                    if (!isAnyOtherFieldFilled && !value) return 'A data final é obrigatória.';
                    if (value && !dateRegex.test(value)) return 'Data inválida';
                    return true;
                  }
                }}
                render={({ field, fieldState }) => (
                  <DateFilterInput
                    label="Data Final"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.error}
                    required={true}
                  />
                )}
              />
            </Col>
            <Col md={3}>
              <InputField
                label="Placa do Veículo"
                name="licensePlate"
                register={register}
                errors={errors}
                placeholder="Placa"
                value={watch('licensePlate') || ''}
              />
            </Col>
            <Col md={3}>
              <Controller
                name="transferOptions"
                control={control}
                rules={{ required: 'Tipo do Relatório é obrigatório.' }}
                render={({ field, fieldState }) => {
                  const licensePlateValue = watch('licensePlate');
                  const driverValue = watch('driver');

                  const options = licensePlateValue || driverValue
                    ? { NOT_IS_TRANSFER: ReportTransfer.NOT_IS_TRANSFER }
                    : ReportTransfer;

                  if ((licensePlateValue || driverValue) && field.value !== 'NOT_IS_TRANSFER') {
                    field.onChange('NOT_IS_TRANSFER');
                  }

                  return (
                    <SelectInput
                      data={options}
                      value={field.value}
                      onChange={field.onChange}
                      label="Tipo do Relatório"
                      isOption={false}
                      isInvalid={!!fieldState.error}
                      errorMessage={fieldState.error?.message}
                    />
                  );
                }}
              />
            </Col>
            <Col md={2}>
              <Controller
                name="reportOrderBy"
                control={control}
                render={({ field, fieldState }) => (
                  <SelectInput
                    data={ReportOrderBy}
                    value={field.value}
                    onChange={field.onChange}
                    label="Ordem de Impressão"
                    isOption={false}
                    isInvalid={!!fieldState.error}
                    errorMessage={fieldState.error?.message}
                  />
                )}
              />
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <Controller
                name="person"
                control={control}
                render={({ field, fieldState }) => (
                  <PersonInput
                    value={field.value}
                    createdNew={false}
                    onChange={(selected) => setValue('person', selected)}
                    onCreated={(created) => {
                      setValue('person', { id: created.id, name: created.name });
                    }}
                    label="Produtor / Cliente"
                    isInvalid={!!fieldState.error}
                    type={['isCustomer', 'isProducer', 'isSupplier' , 'isDriver']}
                  />
                )}
              />
            </Col>
            <Col md={4}>
              <Controller
                name="driver"
                control={control}
                render={({ field, fieldState }) => (
                  <PersonInput
                    value={field.value}
                    createdNew={false}
                    onChange={(selected) => setValue('driver', selected)}
                    onCreated={(created) => {
                      setValue('driver', { id: created.id, name: created.name });
                    }}
                    label="Motorista"
                    isInvalid={!!fieldState.error}
                    type={['isDriver']}
                  />
                )}
              />
            </Col>
            <Col md={4}>
              <Controller
                name="product"
                control={control}
                render={({ field, fieldState }) => (
                  <ProductInput
                    value={field.value}
                    createdNew={false}
                    onChange={(selected) => {
                      setValue('product', selected);
                    }}
                    onCreated={(created) => {
                      setValue('product', { id: created.id, name: created.name });
                    }}
                    label="Produto / Cultura"
                    isInvalid={!!fieldState.error}
                  />
                )}
              />
            </Col>
          </Row>
        </Form.Group>
        <div className="d-flex gap-2">
          <Button
            variant="primary"
            className="button-custom"
            type="submit"
            disabled={isLoading || isLoadingCsv}
          >
            {isLoading ? (
              <>
                Aguarde <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              </>
            ) : (
              'Imprimir PDF'
            )}
          </Button>
          <Button
            variant="success"
            className="button-custom"
            type="button"
            onClick={onExportCsv}
            disabled={isLoading || isLoadingCsv}
          >
            {isLoadingCsv ? (
              <>
                Aguarde <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              </>
            ) : (
              'Exportar CSV'
            )}
          </Button>
        </div>
      </Form>
    </Container>
  );
};

export default Romaneio;